import { useState } from 'react';
import { Check, ChevronDown, MapPin, MessageSquare } from 'lucide-react';

// Parse an assistant turn's content as a stops array. The AI returns a
// top-level JSON array of `{name, address, vibe}` objects per turn which
// we store verbatim in chat_history. Older legacy turns are plain prose,
// in which case this returns null and the caller falls back to showing
// the raw text excerpt.
const parseStopsFromAssistant = (content) => {
  if (!content || typeof content !== 'string') return null;
  const trimmed = content.trim();
  if (!trimmed.startsWith('[')) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    if (!parsed.every((s) => s && typeof s.name === 'string')) return null;
    return parsed;
  } catch {
    return null;
  }
};

// Pair user turns with the assistant response that immediately follows.
// chat_history is flat alternating [user, assistant, user, assistant, ...].
// We walk it once and emit `{ ask, stops?, prose? }` records.
const pairTurns = (chatHistory) => {
  if (!Array.isArray(chatHistory)) return [];
  const pairs = [];
  for (let i = 0; i < chatHistory.length; i += 1) {
    const t = chatHistory[i];
    if (t?.role !== 'user' || !t?.content) continue;
    const next = chatHistory[i + 1];
    let stops = null;
    let prose = null;
    if (next?.role === 'assistant' && next?.content) {
      stops = parseStopsFromAssistant(next.content);
      if (!stops) prose = next.content;
    }
    pairs.push({ ask: t.content, stops, prose });
  }
  return pairs;
};

/**
 * RefinementHistoryAccordion — collapsible "Expand for Full Itinerary"
 * accordion that shows each user query paired with the structured stops
 * the AI returned for THAT turn.
 *
 * Used in two places:
 *   1. `pages/TripPage.jsx`        — the shared /trip/{slug} permalink
 *   2. `components/home/HeroSection` — the live planner conversation,
 *                                      so users can review prior turns
 *                                      without leaving the conversation
 *
 * Props:
 *   - chatHistory: [{role, content}] flat array (same shape backend stores)
 *   - finalLabel:  override copy for the bottom "final itinerary" row.
 *                  Default makes sense on /trip/{slug} where the final
 *                  itinerary is the page hero; the planner caller passes
 *                  a slightly different label.
 *   - defaultOpen: open by default (TripPage = false, planner = false).
 *   - showFinalRow: whether to show the bottom "Final itinerary ✓" row.
 *                   We hide it on the planner where the result panel is
 *                   already adjacent — feels redundant otherwise.
 *
 * Hides entirely when chatHistory yields no user turns to render.
 */
const RefinementHistoryAccordion = ({
  chatHistory,
  finalLabel = null,
  defaultOpen = false,
  showFinalRow = true,
  finalStopCount = 0,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const turns = pairTurns(chatHistory);
  if (turns.length === 0) return null;

  return (
    <div
      className="rounded-2xl border border-gray-200 bg-gray-50/60 overflow-hidden"
      data-testid="trip-history-accordion"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-gray-100/60 transition-colors"
        aria-expanded={open}
        data-testid="trip-history-toggle"
      >
        <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em] text-wine-700">
          <MessageSquare className="w-3.5 h-3.5" />
          Expand for Full Trip Plan
        </span>
        <ChevronDown
          className={`w-4 h-4 text-wine-600 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ol
          className="px-4 pb-4 pt-1 space-y-3"
          data-testid="trip-history-list"
          aria-label="Refinement history"
        >
          {turns.map((turn, i) => (
            <li
              key={i}
              className="rounded-lg bg-white border border-gray-200 px-3 py-2.5 hover:border-wine-200 transition-colors"
              data-testid={`trip-history-turn-${i}`}
            >
              <div className="flex items-start gap-3">
                <span
                  className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-wine-50 border border-wine-200 text-wine-700 text-[11px] font-bold tabular-nums"
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-gray-400 mb-0.5">
                    {i === 0 ? 'Original ask' : `Refinement ${i}`}
                  </div>
                  <p className="text-[13px] text-[#1a1a2e] leading-snug break-words">
                    {turn.ask?.length > 220 ? `${turn.ask.slice(0, 220).trim()}…` : turn.ask}
                  </p>
                </div>
              </div>

              {/* Structured stop cards parsed from the assistant turn. */}
              {turn.stops && turn.stops.length > 0 && (
                <div className="mt-2 pl-9 space-y-1.5" data-testid={`trip-history-stops-${i}`}>
                  {turn.stops.map((s, j) => (
                    <div
                      key={j}
                      className="flex items-start gap-2 rounded-md bg-gold-50/40 border border-gold-200/60 px-2.5 py-1.5"
                    >
                      <span
                        className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-gold-300 text-[#1a1a2e] text-[10px] font-bold tabular-nums mt-0.5"
                        aria-hidden="true"
                      >
                        {j + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-bold text-[#1a1a2e] truncate" title={s.name}>
                          {s.name}
                        </div>
                        {s.address && (
                          <div className="text-[10px] text-gray-500 truncate" title={s.address}>
                            <MapPin className="inline w-2.5 h-2.5 mr-0.5 -mt-0.5" />
                            {s.address}
                          </div>
                        )}
                        {s.vibe && (
                          <p className="text-[11px] text-gray-600 leading-snug mt-0.5">
                            {s.vibe.length > 160 ? `${s.vibe.slice(0, 160).trim()}…` : s.vibe}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Legacy: pre-JSON-format trips stored assistant turns as
                  plain prose. Show a truncated excerpt so the row isn't
                  empty for older shared trips. */}
              {!turn.stops && turn.prose && (
                <div className="mt-2 pl-9">
                  <p className="text-[11px] text-gray-500 italic leading-snug">
                    {turn.prose.length > 280 ? `${turn.prose.slice(0, 280).trim()}…` : turn.prose}
                  </p>
                </div>
              )}
            </li>
          ))}

          {showFinalRow && (
            <li
              className="flex items-start gap-3 rounded-lg bg-wine-50/60 border border-wine-200 px-3 py-2.5"
              aria-label="Final trip plan indicator"
            >
              <span className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-wine-600 text-white" aria-hidden="true">
                <Check className="w-3.5 h-3.5" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-wine-700 mb-0.5">
                  Final trip plan
                </div>
                <p className="text-[13px] text-wine-900 leading-snug">
                  {finalLabel || `The ${finalStopCount || 'three'}-stop trip shown above.`}
                </p>
              </div>
            </li>
          )}
        </ol>
      )}
    </div>
  );
};

export default RefinementHistoryAccordion;
